import { AxiosResponse } from 'axios'
import { ALERTS_LIST_DEFAULT } from 'core/common/constants/alerts'
import { ALERTS_REQUEST_TIMEOUT } from 'core/common/constants/settings'
import Api from 'core/services/api'
import { TPagination } from 'models/Pagination'
import { TProduct } from 'models/Product'
import {
  findProductsByGroupRequest,
  findProductsRequest,
  findProductByIdRequest,
  findProductPromotionsRequest,
  findProductsCompatibleRequest,
  linkProductsRequest,
  getProductHistoryRequest,
  updateProductsRequest,
  createProductsRequest,
  updateProductRequest,
  getProductsAlertsRequest,
  unlinkProductsRequest,
  findProductStocksRequest,
  findValidationMessageRequest,
  getProductMetricsRequest,
  reprocessProductRequest,
  inactiveProductRequest,
  doFreightSimulateRequest,
  updateProductsInGroupRequest,
  TProductPerformanceResponse,
  CreateCustomProductRequest,
  QueryParams,
} from '../../models/products'
import mappingStatus from './adapters/mappingStatus'

const path = '/v1/products'
const pathFullProduct = '/v1/core/products'

export const findProducts = (query: findProductsRequest = {}) => {
  return Api.request<TProduct[], AxiosResponse<TProduct[]> & { pagination: TPagination }>({
    url: path,
    params: { ...mappingStatus(query) },
  })
}

export const findProductsByGroup = (query: findProductsByGroupRequest = {}) => {
  return Api.request({
    url: `${path}/group`,
    params: { ...mappingStatus(query) },
  })
}

export const findProductById = (params: findProductByIdRequest = {}) => {
  const { id, ...other } = params

  return Api.request({
    url: `${path}/${id}`,
    params: { ...other },
    method: 'get',
  })
}

export const findProductPromotions = ({ sku }: findProductPromotionsRequest) => {
  return Api.request({
    url: `/v1/promotions/promo3p/${encodeURIComponent(sku)}`,
  })
}

export const findProductsCompatible = ({
  attributes,
  query = {},
}: findProductsCompatibleRequest) => {
  return Api.request({
    url: `${path}/compatible`,
    params: {
      attributes: attributes.join(','),
      ...query,
    },
  })
}

export const linkProducts = ({ productsIds, groupId, query = {} }: linkProductsRequest) => {
  return Api.request({
    url: `${path}/link`,
    method: 'PATCH',
    data: {
      product_ids: productsIds,
      group_id: groupId,
    },
    params: { ...query },
  })
}

export const createProducts = ({ form, query = {} }: createProductsRequest) => {
  return Api.request({
    url: path,
    method: 'POST',
    data: form,
    params: { ...query },
  })
}

export const createCustomProducts = ({
  form,
  query = {},
}: {
  form: CreateCustomProductRequest
  query?: QueryParams
}) => {
  return Api.request({
    url: pathFullProduct,
    method: 'POST',
    data: form,
    params: query,
  })
}

export const getProductHistory = (query: getProductHistoryRequest = {}) => {
  return Api.request({
    url: `${path}/history`,
    params: { ...query },
  })
}

export const updateProduct = ({ form, id }: updateProductRequest) => {
  return Api.request({
    url: `${path}/${id}`,
    method: 'PATCH',
    data: form,
  })
}

export const getProductsAlerts = ({
  fulfilled = false,
  status__ne = '',
}: getProductsAlertsRequest) => {
  const items = fulfilled
    ? Promise.resolve({ data: ALERTS_LIST_DEFAULT })
    : Api.request({
        url: `${path}/alerts`,
        timeout: ALERTS_REQUEST_TIMEOUT,
        params: { status__ne },
      })
  return items
}

export const updateProducts = ({ products, query = {} }: updateProductsRequest) => {
  return Api.request({
    url: path,
    method: 'PATCH',
    data: { products },
    params: { ...query },
  })
}

export const updateProductsInGroup = ({ form, groupId }: updateProductsInGroupRequest) => {
  return Api.request({
    url: `${path}/group/${groupId}`,
    method: 'PUT',
    data: form,
  })
}

export const unlinkProducts = ({ productsIds, groupId, query = {} }: unlinkProductsRequest) => {
  return Api.request({
    url: `${path}/unlink`,
    method: 'PATCH',
    data: {
      product_ids: productsIds,
      group_id: groupId,
    },
    params: { ...query },
  })
}

export const findProductStocks = ({ id }: findProductStocksRequest) => {
  return Api.request({
    url: `${path}/${id}/stocks`,
    method: 'get',
  })
}

export const findValidationMessage = ({ id }: findValidationMessageRequest) => {
  return Api.request({
    url: `${path}/validation/message/${id}`,
    method: 'get',
  })
}

export const reprocessProduct = ({ id_or_sku }: reprocessProductRequest) => {
  return Api.request({
    url: `${path}/reprocess/${id_or_sku}`,
    method: 'post',
  })
}

export const inactiveProduct = ({ id_or_sku }: inactiveProductRequest) => {
  return Api.request({
    url: `${path}/inactivate/${id_or_sku}`,
    method: 'post',
  })
}

export const getProductMetrics = ({ sku, id }: getProductMetricsRequest) => {
  return Api.request({
    url: '/v1/product-metrics',
    params: {
      sku,
      id,
    },
  })
}

export const doFreightSimulate = (skuData: doFreightSimulateRequest) => {
  return Api.request({
    url: '/v1/freight/simulator',
    method: 'post',
    data: skuData,
  })
}

export const getCommissionData = (sku: string) => {
  return Api.request({
    url: `/v1/commission/seller?sku=${sku}`,
  })
}

export const getProductPerformance = (sku: string, days: number) => {
  return Api.request<TProductPerformanceResponse>({
    url: '/v1/performance/sku',
    params: { sku, days },
  })
}

export const getMigrationStatus = () => {
  return Api.request({
    url: '/v1/seller/migration-status',
  })
}

export const getSeller = () => {
  return Api.request({
    url: '/v1/admin/seller',
  })
}
